import { THEME_OPTIONS } from "../components/properties/fonts";
import _ from "lodash";
import { parseColor } from "./color";

export function calculateFontSizeForMobile(fontSize) {
  const value = Math.round(fontSize * 0.4848);
  return Math.max(13, value);
}

export const cleanFontSizeFromElement = (editor, node) => {
  const dom = editor.dom;
  processChildElements(node,
    (node) => hasStyle(dom, node, 'fontSize'),
    (node) => applyStyle(dom, node, 'fontSize', '')
  );
};

const processChildElements = (node, filter, process) => {
  Array.from(node.childNodes).forEach(child => {
    if (child) {
      if (filter(child)) {
        process(child);
      }
      if (child.hasChildNodes()) {
        processChildElements(child, filter, process);
      }
    }
  });
};

const hasStyle = (dom, node, name) => !!(node && dom.getStyle(node, name));

const applyStyle = (dom, node, name, value) => {
  dom.setStyle(node, name, value);

  if (node.getAttribute('style') === '') {
    node.removeAttribute('style');
  }

  unwrapEmptySpan(dom, node);
};

const unwrapEmptySpan = (dom, node) => {
  if (node.nodeName === 'SPAN' && dom.getAttribs(node).length === 0) {
    dom.remove(node, true);
  }
};

//Get Actions for text widget
export function getActionsFromHTML(content) {
  const node = document.createElement('div');
  node.innerHTML = content;

  const anchors = node.querySelectorAll('a[data-action]');
  return Array.from(anchors).map(element => {
    return element.dataset.action;
  })
}

export function isAllContentSelected(textEditor) {
  if(!textEditor){
    return false;
  }

  var selectedNode = textEditor.selection.getNode();
  var selectedText = null;
  if(selectedNode){
    selectedText = selectedNode.textContent;
  }

  return textEditor.bodyElement.textContent === selectedText;
}

export function isEditorContentFullySelected(textEditor) {
  if (!textEditor) {
    return false;
  }

  const selection = textEditor.selection.getSel();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }

  const range = selection.getRangeAt(0);
  const body = textEditor.getBody();
  
  // Check if the range encompasses the entire body
  const isFullRange = 
    range.startContainer === body && 
    range.endContainer === body &&
    range.startOffset === 0 && 
    range.endOffset === body.childNodes.length;
  
  if (isFullRange) {
    return true;
  }
  
  // Fallback: compare text content lengths
  const selectedText = selection.toString().trim();
  const fullText = (textEditor.bodyElement.textContent || '').trim();
  
  return selectedText === fullText && selectedText.length > 0;
}

export function generateNodeFromSelection(textEditor){
  var startContainer = textEditor.selection.getStart();

  const allContentSelected = isAllContentSelected(textEditor);
  if(allContentSelected){
    startContainer = selectFirstNodeWithText(startContainer);
  }

  const span = generateNodeFromSelectedNode(textEditor.dom, startContainer);
  return span;
}

//Get Styles for node
export function generateNodeFromSelectedNode(dom, node){
  if(!node){
    return null;
  }

  const span = dom.create('span');

  const fontSize = dom.getStyle(node, 'fontSize', true);
  const fontFamily = dom.getStyle(node, 'fontFamily', true);
  const color = dom.getStyle(node, 'color', true);
  const background = dom.getStyle(node, 'background-color', true);

  const fontWeight = dom.getStyle(node, 'font-weight', true);
  const italics = dom.getStyle(node, 'font-style', true);
  const underline = dom.getStyle(node, 'text-decoration', true);

  if(!!fontSize){
    dom.setStyle(span, 'font-size', fontSize);
  }

  if(!!fontFamily){
    dom.setStyle(span, 'font-family', fontFamily);
  }

  if(!!fontWeight){
    dom.setStyle(span, 'font-weight', fontWeight);
  }

  if(!!color){
    dom.setStyle(span, 'color', color);
  }

  if(!!background){
    dom.setStyle(span, 'background-color', background);
  }

  if(!!italics){
    dom.setStyle(span, 'font-style', italics);
  }

  if(!!underline){
    dom.setStyle(span, 'text-decoration', underline);
  }

  return span;
}

function selectFirstNodeWithText(root) {
  if(!root){
    return root;
  }

  if (root.nodeType === document.TEXT_NODE) {
    var text = root.nodeValue;
    return root.parentElement;
  } else if (root.nodeType === document.ELEMENT_NODE) {
    for (var i = 0; i < root.childNodes.length; i++) {
      const child = root.childNodes[i];
      const result = selectFirstNodeWithText(child)
      if (result) {
        return result;
      }
    }
  }

  return null;
};

export function preserveStylesOnDelete(textEditor, targetNode) {
  // Get the current selection node before deletion
  const selectedNode = textEditor.selection.getNode();
  const rootBlock = getRootBlockFromNode(selectedNode);
  
  if (!rootBlock) {
    return null;
  }

  // Clone the root block to preserve its structure
  const clonedBlock = rootBlock.cloneNode(true);
  
  // Function to recursively clear text but keep structure
  const clearTextKeepStructure = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      node.nodeValue = '\u200B'; // Zero-width space
      return;
    }
    
    if (node.nodeType === Node.ELEMENT_NODE) {
      // Keep the element structure but clear text from children
      Array.from(node.childNodes).forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          child.nodeValue = '\u200B';
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          // Recursively process child elements
          if (child.childNodes.length > 0) {
            clearTextKeepStructure(child);
          } else {
            // Empty element, add zero-width space
            child.innerHTML = '\u200B';
          }
        }
      });
      
      // If the element is now empty, add a zero-width space
      if (node.childNodes.length === 0) {
        node.innerHTML = '\u200B';
      }
    }
  };
  
  clearTextKeepStructure(clonedBlock);
  
  return clonedBlock.outerHTML;
}

export function handleEmptyContentInEditor(textEditor, target) {
  // First, try to preserve the existing structure
  const preservedContent = preserveStylesOnDelete(textEditor, target);
  if (preservedContent) {
    return preservedContent;
  }
  
  // Fallback to original logic if preservation fails
  var startContainer = selectFirstNodeWithText(target);
  var span = generateNodeFromSelectedNode(textEditor.dom, startContainer);

  if (!span) {
    span = textEditor.dom.create('span');
  }
  
  var format = 'p';
  const rootBlock = getRootBlockFromSelection(textEditor);
  if (rootBlock) {
    format = rootBlock.nodeName.toLowerCase();
  }

  var formatElement = textEditor.dom.create(format);
  
  if (span) {
    span.innerHTML = '\u200B'; // Use zero-width space instead of &#8203;
    formatElement.appendChild(span);
  }
  
  return formatElement.outerHTML;
}

export function getRootBlockFromSelection(textEditor){
  const node = textEditor.selection.getNode();
  const root = getRootBlockFromNode(node);
  return root;
}

export function getRootBlockFromNode(node){
  if(!node){
    return node;
  }

  if(isRootBlock(node)){
    return node;
  }

  const parentBlock = findRootBlockInAncestors(node.parentElement);
  if(parentBlock){
    return parentBlock;
  }

  return findRootBlockFromChildren(node);
}

function findRootBlockInAncestors(node){
  if(!node){
    return null;
  }

  if(isRootBlock(node)){
    return node;
  }

  return findRootBlockInAncestors(node.parentElement);
}

function findRootBlockFromChildren(node){
  if(!node){
    return null;
  }

  var childRoot = null;
  for(var i = 0; i < node.childNodes.length; i ++){
    const child = node.childNodes[0];

    if(isRootBlock(child)){
      childRoot = child;
      break;
    }

    const rootBlock = findRootBlockFromChildren(child);
    if(rootBlock){
      childRoot = rootBlock;
      break;
    }
  }

  return childRoot;
}

function isRootBlock(node){
  if(!node){
    return false;
  }

  const nodeName = node.nodeName.toLowerCase();
  return THEME_OPTIONS.find(theme => {
    return theme.value === nodeName;
  });
}

/**
 * Generate theme from the node
 * 
*/
export function getThemeFromNode(textEditor, node) {
  const dom = textEditor.dom;
  const selectedNode = selectFirstNodeWithText(node);

  const fontSize = dom.getStyle(selectedNode, 'fontSize', true);
  const fontFamily = dom.getStyle(selectedNode, 'fontFamily', true);
  const color = dom.getStyle(selectedNode, 'color', true);

  const lineHeight = dom.getStyle(selectedNode, 'line-height', true);
  var lineSpacing = parseFloat(lineHeight) / parseFloat(fontSize);
  lineSpacing = lineSpacing.toFixed(1);

  var letterSpacing = dom.getStyle(selectedNode, 'letter-spacing', true);
  if(letterSpacing === 'normal'){
    letterSpacing = 0;
  }else {
    letterSpacing = parseFloat(letterSpacing) / parseFloat(fontSize);
    letterSpacing = letterSpacing.toFixed(1);
  }

  var fontFormat = 'p';
  const rootBlock = getRootBlockFromSelection(textEditor);
  if (rootBlock) {
    fontFormat = rootBlock.nodeName.toLowerCase();
  }

  const fontWeight = dom.getStyle(selectedNode, 'font-weight', true);
  const fontStyle = dom.getStyle(selectedNode, 'font-style', true);

  var fontStyles = {
    style: {
      bold: fontWeight >= 600,
      italic: fontStyle === 'italic',
      underline: false
    },
    fontSize: parseFloat(fontSize).toString(),
    lineSpacing: lineSpacing.toString(),
    letterSpacing: parseFloat(letterSpacing).toString(),
    fontFamily: fontFamily,
    color: parseColor(color)
  }

  return {
    type: fontFormat,
    font: fontStyles
  };
}

/**
 * Update content for personalisation
 * 
*/
export function updateContentForVariables(content, variables, variableMappings) {

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const el = doc.querySelector('.mce-variable');
  if (!el) return content;

  const variableId = el.getAttribute('data-variable-id');

  const isIncluded = Array.isArray(variables)
    ? variables?.includes(variableId)
    : variables && typeof variables === 'object'
      ? Object.prototype.hasOwnProperty.call(variables, variableId)
      : false;

  if (variableId && !isIncluded) {
    return content
      .replace(/<span[^>]*class="[^"]*\bvariable-wrapper\b[^"]*"[^>]*>(.*?)<\/span>/g, '$1')
      .replace(/<span[^>]*\sdata-variable-id=["'][^"']*["'][^>]*>(.*?)<\/span>/g, '$1');
  }

  if (!Array.isArray(variableMappings) || variableMappings.length === 0) {
    return content;
  }

  const selected = variableMappings.find(item => item.id === variableId);
  if (!selected) return content;

  return content
  .replace(/data-original-variable="[^"]*"/, `data-original-variable="${selected.name}"`)
  .replace(/>([^<]*)<\/span>/, `>${selected.name}</span>`);
}

const distributeStrike = (node) => {
  const fragment = document.createDocumentFragment();
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.textContent.length > 0) {
        const s = document.createElement("s");
        s.appendChild(child.cloneNode());
        fragment.appendChild(s);
      }
    } else if (child.nodeName === "SUP" || child.nodeName === "SUB") {
      const s = document.createElement("s");
      s.innerHTML = child.innerHTML;
      const v = child.cloneNode(false);
      v.appendChild(s);
      fragment.appendChild(v);
    } else {
      const s = document.createElement("s");
      s.appendChild(child.cloneNode(true));
      fragment.appendChild(s);
    }
  });
  node.innerHTML = "";
  Array.from(fragment.childNodes).forEach((n) => node.appendChild(n));
};

export const normalizeStrikeNesting = (editor) => {
  const body = editor.getBody();

  // Case E: text-decoration:line-through on the editor body root
  const bodyTd = body.style?.textDecoration || "";
  if (bodyTd.includes("line-through")) {
    body.style.textDecoration = bodyTd
      .replace(/\bline-through\b/, "")
      .replace(/\s+/g, " ")
      .trim();
    Array.from(body.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) distributeStrike(child);
    });
  }

  // Case A: text-decoration:line-through on a block element
  body.querySelectorAll("h1,h2,h3,h4,h5,h6,p,li,td,th,div").forEach((block) => {
    const td = block.style.textDecoration || "";
    if (!td.includes("line-through")) return;
    block.style.textDecoration = td
      .replace(/\bline-through\b/, "")
      .replace(/\s+/g, " ")
      .trim();
    distributeStrike(block);
  });

  // Case D: <s> directly wrapping a block element
  body
    .querySelectorAll("s > h1, s > h2, s > h3, s > h4, s > h5, s > h6, s > p, s > div")
    .forEach((blockEl) => {
      const sEl = blockEl.parentElement;
      distributeStrike(blockEl);
      sEl.parentElement.replaceChild(blockEl, sEl);
    });

  // Case F: <s> wrapping a <span> that contains <sup>/<sub>
  body.querySelectorAll("s > span").forEach((spanEl) => {
    if (!spanEl.querySelector("sup, sub")) return;
    const sEl = spanEl.parentElement;
    distributeStrike(spanEl);
    sEl.parentElement.replaceChild(spanEl, sEl);
  });

  // Case B: <s> directly containing <sup>/<sub>
  const wrongWrappers = body.querySelectorAll("s > sup, s > sub");
  if (wrongWrappers.length > 0) {
    const sParents = new Set();
    wrongWrappers.forEach((v) => sParents.add(v.parentElement));
    sParents.forEach((sEl) => {
      const fragment = document.createDocumentFragment();
      Array.from(sEl.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          if (child.textContent.length > 0) {
            const s = document.createElement("s");
            s.appendChild(child.cloneNode());
            fragment.appendChild(s);
          }
        } else if (child.nodeName === "SUP" || child.nodeName === "SUB") {
          const s = document.createElement("s");
          s.innerHTML = child.innerHTML;
          const v = child.cloneNode(false);
          v.appendChild(s);
          fragment.appendChild(v);
        } else {
          const s = document.createElement("s");
          s.appendChild(child.cloneNode(true));
          fragment.appendChild(s);
        }
      });
      sEl.parentElement.replaceChild(fragment, sEl);
    });
  }

  // Case C: bare <sup>/<sub> adjacent to <s> siblings
  body.querySelectorAll("sup, sub").forEach((vertEl) => {
    const prev = vertEl.previousElementSibling;
    const next = vertEl.nextElementSibling;
    if (
      (prev && prev.nodeName === "S") ||
      (next && next.nodeName === "S")
    ) {
      const s = document.createElement("s");
      s.innerHTML = vertEl.innerHTML;
      const v = vertEl.cloneNode(false);
      v.appendChild(s);
      vertEl.parentElement.replaceChild(v, vertEl);
    }
  });
};
